import React, { useState, useEffect } from 'react';
import { Modal, Select, Button, message, Space, Typography, Table } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../config';

const { Text, Title } = Typography;
const { Option } = Select;

interface BulkPushFloorsModalProps {
  visible: boolean;
  hotelId: number;
  emHotelId: number;
  onClose: () => void;
  onSuccess: () => void;
}

interface EmLanguage {
  id: number;
  code: string;
  label: string;
}

interface LanguageMapping {
  mewsLanguage: string;
  emLanguageId: number;
}

const MEWS_LANGUAGES = [
  { code: 'en-US', label: 'English (US)' },
  { code: 'de-DE', label: 'German' },
  { code: 'fr-FR', label: 'French' },
  { code: 'it-IT', label: 'Italian' },
  { code: 'es-ES', label: 'Spanish' },
  { code: 'nl-NL', label: 'Dutch' },
];

const LANGUAGE_MATCH_MAP: Record<string, string[]> = {
  'en-US': ['english', 'en', 'en-us', 'en-gb'],
  'en-GB': ['english', 'en', 'en-us', 'en-gb'],
  'nl-NL': ['dutch', 'nl', 'nl-nl', 'nederlands'],
  'de-DE': ['german', 'de', 'de-de', 'deutsch'],
  'fr-FR': ['french', 'fr', 'fr-fr', 'français'],
  'es-ES': ['spanish', 'es', 'es-es', 'español'],
  'it-IT': ['italian', 'it', 'it-it', 'italiano'],
};

function autoMatchLanguages(
  mewsLanguages: { code: string; label: string }[],
  emLanguages: { id: number; code: string }[],
): LanguageMapping[] {
  const matched: LanguageMapping[] = [];
  for (const mewsLang of mewsLanguages) {
    const possibleMatches = LANGUAGE_MATCH_MAP[mewsLang.code] || [mewsLang.code.split('-')[0], mewsLang.code];
    const emMatch = emLanguages.find(emLang =>
      possibleMatches.some(m => emLang.code.toLowerCase() === m)
    );
    if (emMatch) {
      matched.push({
        mewsLanguage: mewsLang.code,
        emLanguageId: emMatch.id,
      });
    }
  }
  return matched;
}

const BulkPushFloorsModal: React.FC<BulkPushFloorsModalProps> = ({
  visible,
  hotelId,
  emHotelId,
  onClose,
  onSuccess,
}) => {
  const [emLanguages, setEmLanguages] = useState<EmLanguage[]>([]);
  const [loading, setLoading] = useState(false);
  const [floorCount, setFloorCount] = useState(0);
  const [floors, setFloors] = useState<any[]>([]);
  const [languageMappings, setLanguageMappings] = useState<LanguageMapping[]>([
    { mewsLanguage: '', emLanguageId: 0 },
  ]);

  useEffect(() => {
    if (visible && hotelId !== undefined) {
      loadEmLanguages();
      loadFloorData();
    } else if (!visible) {
      // Reset on close
      setFloors([]);
      setFloorCount(0);
      setLanguageMappings([{ mewsLanguage: '', emLanguageId: 0 }]);
    }
  }, [visible, hotelId]);

  const loadEmLanguages = async () => {
    try {
      const response = await axios.get(`${API_URL}/em-push/languages`);
      const langs = response.data;
      setEmLanguages(langs);

      // Auto-match language mappings
      const matched = autoMatchLanguages(MEWS_LANGUAGES, langs);
      if (matched.length > 0) {
        setLanguageMappings(matched);
      }
    } catch (error) {
      console.error('Failed to load EM languages:', error);
      message.error('Failed to load EM languages');
    }
  };

  const loadFloorData = async () => {
    try {
      // Fetch all floors for this hotel
      const response = await axios.get(`${API_URL}/hotels/${hotelId}/floors`);
      const floorsData = response.data;

      setFloorCount(floorsData.length);
      setFloors(floorsData);
    } catch (error) {
      console.error('Failed to load floors:', error);
      message.error('Failed to load floors');
    }
  };

  const handleAddMapping = () => {
    setLanguageMappings([...languageMappings, { mewsLanguage: '', emLanguageId: 0 }]);
  };

  const handleRemoveMapping = (index: number) => {
    const newMappings = languageMappings.filter((_, i) => i !== index);
    setLanguageMappings(newMappings);
  };

  const handleMewsLanguageChange = (index: number, value: string) => {
    const newMappings = [...languageMappings];
    newMappings[index].mewsLanguage = value;
    setLanguageMappings(newMappings);
  };

  const handleEmLanguageChange = (index: number, value: number) => {
    const newMappings = [...languageMappings];
    newMappings[index].emLanguageId = value;
    setLanguageMappings(newMappings);
  };

  const getUsedMewsLanguages = () => {
    return languageMappings
      .filter(m => m.mewsLanguage)
      .map(m => m.mewsLanguage);
  };

  const getUsedEmLanguages = () => {
    return languageMappings
      .filter(m => m.emLanguageId)
      .map(m => m.emLanguageId);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Filter out incomplete mappings
      const validMappings = languageMappings.filter(
        m => m.mewsLanguage && m.emLanguageId
      );

      if (validMappings.length === 0) {
        message.warning('Please add at least one complete language mapping');
        setLoading(false);
        return;
      }

      // Call bulk push endpoint
      const response = await axios.post(`${API_URL}/floors/bulk-push-to-em`, {
        hotelId,
        emHotelId,
        languageMappings: validMappings,
      });

      const { created, updated, failed } = response.data;

      if (created > 0 || updated > 0) {
        message.success(
          `Successfully pushed floors to EM! Created: ${created}, Updated: ${updated}${failed > 0 ? `, Failed: ${failed}` : ''}`
        );
        onSuccess();
        onClose();
      } else if (failed > 0) {
        message.error(`Failed to push ${failed} floors to EM`);
      }
    } catch (error: any) {
      console.error('Bulk push error:', error);
      message.error(error.response?.data?.message || 'Failed to push floors to EM');
    } finally {
      setLoading(false);
    }
  };

  const previewColumns = [
    {
      title: 'Floor Number',
      dataIndex: 'floorNumber',
      key: 'floorNumber',
      width: 120,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Room Count',
      dataIndex: 'roomCount',
      key: 'roomCount',
      width: 120,
    },
    {
      title: 'EM Status',
      key: 'emStatus',
      width: 120,
      render: (record: any) => (
        <Text type={record.emFloorId ? 'success' : 'secondary'}>
          {record.emFloorId ? 'In EM' : 'Not in EM'}
        </Text>
      ),
    },
  ];

  return (
    <Modal
      title="Bulk Push All Floors to EM"
      open={visible}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          disabled={floorCount === 0}
        >
          Push {floorCount} Floor{floorCount !== 1 ? 's' : ''} to EM
        </Button>,
      ]}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
        },
      }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Text strong>Number of Floors: </Text>
          <Text>{floorCount}</Text>
        </div>

        {floorCount > 0 && (
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Floor Preview
            </Text>
            <Table
              columns={previewColumns}
              dataSource={floors}
              rowKey="id"
              size="small"
              pagination={false}
              scroll={{ y: 200 }}
            />
          </div>
        )}

        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Language Mappings (Source to EM)
          </Text>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 12 }}>
            Map source languages to EM languages. Floor names will be synced according to these mappings.
          </Text>

          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {languageMappings.map((mapping, index) => (
              <Space key={index} align="start" style={{ width: '100%' }}>
                <Select
                  style={{ width: 200 }}
                  placeholder="Select Source Language"
                  value={mapping.mewsLanguage || undefined}
                  onChange={(value) => handleMewsLanguageChange(index, value)}
                >
                  {MEWS_LANGUAGES.map((lang) => (
                    <Option
                      key={lang.code}
                      value={lang.code}
                      disabled={getUsedMewsLanguages().includes(lang.code) && mapping.mewsLanguage !== lang.code}
                    >
                      {lang.label} ({lang.code})
                    </Option>
                  ))}
                </Select>

                <Text style={{ padding: '4px 8px' }}>→</Text>

                <Select
                  style={{ width: 200 }}
                  placeholder="Select EM Language"
                  value={mapping.emLanguageId || undefined}
                  onChange={(value) => handleEmLanguageChange(index, value)}
                >
                  {emLanguages.map((lang) => (
                    <Option
                      key={lang.id}
                      value={lang.id}
                      disabled={getUsedEmLanguages().includes(lang.id) && mapping.emLanguageId !== lang.id}
                    >
                      {lang.label}
                    </Option>
                  ))}
                </Select>

                {languageMappings.length > 1 && (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveMapping(index)}
                  />
                )}
              </Space>
            ))}

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddMapping}
              style={{ width: '100%', marginTop: 8 }}
            >
              Add Language Mapping
            </Button>
          </Space>
        </div>
      </Space>
    </Modal>
  );
};

export default BulkPushFloorsModal;
